'use client'

/**
 * Safe Refresh (Issue #3781)
 *
 * Flushes all draft/submission state to localStorage before
 * reloading the page, preventing data loss on refresh.
 */

import { DRAFT_PREFIX } from '@/hooks/use-form-draft'
import { hasPendingSubmissions } from '@/lib/submission-cache'

const RECOVERY_FLAG = 'cadence-pending-recovery'
const beforeRefreshCallbacks: Set<() => void> = new Set()
const pendingWorkProviders: Set<() => boolean> = new Set()

/**
 * Register a provider that signals in-memory pending work to hasPendingWork().
 * Use this when a component holds unsaved state that is NOT persisted to
 * localStorage (e.g. a CodeMirror editor backed by a React ref).
 *
 * @returns An unregister function — call it on component unmount.
 *
 * @example
 * useEffect(() => {
 *   return registerPendingWorkProvider(() => hasUserEditedRef.current)
 * }, [])
 */
export function registerPendingWorkProvider(
  provider: () => boolean,
): () => void {
  pendingWorkProviders.add(provider)
  return () => {
    pendingWorkProviders.delete(provider)
  }
}

/**
 * Register a callback to run before a safe refresh.
 * Used by forms to flush debounced state to localStorage.
 * Returns an unregister function.
 */
export function registerBeforeRefresh(cb: () => void): () => void {
  beforeRefreshCallbacks.add(cb)
  return () => {
    beforeRefreshCallbacks.delete(cb)
  }
}

/** Prefix used by performance review wizard drafts (Issue #5250) */
const WIZARD_DRAFT_PREFIX = 'performance-review-draft-'
/** Prefix used by performance review drawer drafts (Issue #5250) */
const DRAWER_DRAFT_PREFIX = 'review-draft-'

/**
 * Check if there are any pending drafts in localStorage.
 * Checks both the general form draft prefix and the performance
 * review wizard draft prefix (Issue #5250).
 */
function hasPendingDrafts(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (
        key?.startsWith(DRAFT_PREFIX) ||
        key?.startsWith(WIZARD_DRAFT_PREFIX) ||
        key?.startsWith(DRAWER_DRAFT_PREFIX)
      ) {
        return true
      }
    }
  } catch {
    // localStorage unavailable
  }
  return false
}

/**
 * Check if there is any pending work (drafts or submissions)
 * that would be at risk during a page reload.
 */
export function hasPendingWork(): boolean {
  if (hasPendingDrafts() || hasPendingSubmissions()) return true
  for (const provider of pendingWorkProviders) {
    try {
      if (provider()) return true
    } catch {
      // Don't let one provider's failure prevent the check from completing
    }
  }
  return false
}

/**
 * Check if a recovery just happened (page was reloaded via safeRefresh).
 * Clears the flag after reading.
 */
export function checkRecoveryFlag(): boolean {
  try {
    const flag = sessionStorage.getItem(RECOVERY_FLAG)
    if (flag) {
      sessionStorage.removeItem(RECOVERY_FLAG)
      return true
    }
  } catch {
    // sessionStorage unavailable
  }
  return false
}

/**
 * Safely refresh the page by flushing all pending state first.
 *
 * 1. Calls all registered beforeRefresh callbacks (forms flush debounced state)
 * 2. Sets a recovery flag so the app knows to show recovery UI on mount
 * 3. Reloads the page
 */
export function safeRefresh(): void {
  // Flush all registered form state
  for (const cb of beforeRefreshCallbacks) {
    try {
      cb()
    } catch {
      // Don't let one form's failure prevent refresh
    }
  }

  // Set recovery flag
  try {
    sessionStorage.setItem(RECOVERY_FLAG, 'true')
  } catch {
    // sessionStorage unavailable -- proceed anyway
  }

  forcePageReload()
}

/**
 * Force a full page reload.
 *
 * This is the ONLY approved callsite for window.location.reload().
 * The Biome GritQL plugin `no-window-location-reload` bans direct
 * usage everywhere else. If you need a full reload, call this
 * function instead.
 *
 * Valid use cases: demo mode transitions, OAuth callbacks,
 * safeRefresh recovery. Adding new callers requires code owner
 * approval on this file.
 */
export function forcePageReload(): void {
  window.location.reload()
}
